import { useEffect, useLayoutEffect, useRef, type MutableRefObject, type RefObject } from 'react';
import { isExecutableStatement, isLikelyCompleteStatement } from '../../common/utils/peekResultsDetection';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { QueryEditorHandle } from '../components/QueryEditor';
import type { QueryExecuteSource } from './useQueryExecution';
import type { CursorStatement } from './useCursorStatementTracking';
import type { StatementSegmentsSnapshot } from './useStatementSegments';
import { findSegmentAtOffset } from '../utils/statementAtCursor';
import {
  hasActiveQueryTabRefreshTarget,
  refreshIntelliSenseForActiveQueryTab,
} from '../../../intellisense/schemaRefresh/refreshTargets';
import {
  registerKeybindingTarget,
  type KeybindingHandlersRef,
} from '../../../keybindings/dispatch/targetRegistry';
import { createQueryEditorKeybindingEditor, visibleTabRoot } from './useQueryCancelKeybinding';

type UseQueryEditorShortcutsParams = {
  tabId: string;
  activeTabId: string | null;
  engineId: DatabaseEngineId;
  editorRef: RefObject<QueryEditorHandle | null>;
  /** The tab's own outer container (`data-testid="query-editor-tab"`), its keybinding root. */
  rootRef: RefObject<HTMLElement | null>;
  queryRef: MutableRefObject<string>;
  editorSelectedTextRef: MutableRefObject<string>;
  currentCursorStatementRef: MutableRefObject<CursorStatement | null>;
  /** Shared version-keyed segments cache — see `useStatementSegments`. */
  getStatementSegments: () => StatementSegmentsSnapshot | null;
  handleExecute: (source: QueryExecuteSource) => void;
  handleExecuteSelection: (sql: string, source?: QueryExecuteSource) => void;
  /** Runs the Visual view's shown statement; false while the SQL view shows. */
  runVisualStatement?: (trigger: 'shortcut') => boolean;
  handleSaveFile: () => Promise<void>;
  handleSaveFileAs: () => Promise<void>;
  setStatusMessage: (message: string) => void;
  setBottomPaneCollapsed: (updater: (prev: boolean) => boolean) => void;
};

export const QUERY_EDITOR_SHORTCUT_COMMAND_IDS = [
  'query.execute',
  'query.executeCurrentStatement',
  'query.executeAll',
  'query.toggleResults',
  'query.save',
  'query.saveAs',
  'query.refreshIntellisense',
] as const;

export type CurrentStatementExecution = {
  readonly sql: string;
  readonly source: 'keyboard-shortcut-selection' | 'keyboard-shortcut-cursor';
};

export function resolveCurrentStatementExecution({
  selectedText,
  snapshot,
  cursorOffset,
  fallbackCursorStatement,
  setStatusMessage,
}: {
  readonly selectedText: string;
  readonly snapshot: StatementSegmentsSnapshot | null;
  readonly cursorOffset: number | null;
  readonly fallbackCursorStatement: CursorStatement | null;
  readonly setStatusMessage?: (message: string) => void;
}): CurrentStatementExecution | null {
  if (selectedText.trim()) {
    return { sql: selectedText, source: 'keyboard-shortcut-selection' };
  }
  if (!snapshot?.sql.trim()) {
    setStatusMessage?.('No statement to execute.');
    return null;
  }

  if (cursorOffset !== null) {
    const startingAtCursor = snapshot.segments.find(
      (segment) => segment.trimStart === cursorOffset,
    );
    const containing = startingAtCursor ??
      findSegmentAtOffset(snapshot.segments, cursorOffset);
    if (containing) {
      return { sql: containing.text, source: 'keyboard-shortcut-cursor' };
    }
    const nearestAbove = [...snapshot.segments]
      .reverse()
      .find((segment) => segment.trimEnd <= cursorOffset);
    if (nearestAbove) {
      return { sql: nearestAbove.text, source: 'keyboard-shortcut-cursor' };
    }
  }

  if (cursorOffset === null && fallbackCursorStatement) {
    return { sql: fallbackCursorStatement.text, source: 'keyboard-shortcut-cursor' };
  }
  setStatusMessage?.('No statement at cursor.');
  return null;
}

/**
 * Keybinding targets for a query editor tab:
 *   F5          — execute the narrowest available scope:
 *                 1) user selection (if it parses as executable and isn't the
 *                    whole editor), else
 *                 2) the statement under the cursor (if the cursor-statement
 *                    highlight is active), else
 *                 3) fall back to the full editor contents.
 *   Shift+F5    — always execute the full editor contents, regardless of
 *                 selection / cursor position (and of the Visual view).
 * While the Visual view shows, F5 and Cmd/Ctrl+Enter run its shown statement.
 *   Shift+Enter, Cmd/Ctrl+Enter — execute the selection, else the statement
 *                 under or above the cursor; never the whole multi-statement
 *                 buffer (`resolveCurrentStatementExecution`).
 *   Cmd/Ctrl+S  — Save, Cmd/Ctrl+Shift+S — Save As.
 *   Cmd/Ctrl+J  — Toggle the results pane (collapse / restore).
 * Plus the command-palette-dispatched toggle-results-pane event. Behavior
 * carried verbatim from the previous inline implementation.
 */
export function useQueryEditorShortcuts({
  tabId,
  activeTabId,
  engineId,
  editorRef,
  rootRef,
  queryRef,
  editorSelectedTextRef,
  currentCursorStatementRef,
  getStatementSegments,
  handleExecute,
  handleExecuteSelection,
  runVisualStatement,
  handleSaveFile,
  handleSaveFileAs,
  setStatusMessage,
  setBottomPaneCollapsed,
}: UseQueryEditorShortcutsParams) {
  const handlersRef = useRef<KeybindingHandlersRef>({ current: {} });
  handlersRef.current.current = {
    'query.execute': () => {
      // The Visual view runs its shown statement — never the hidden editor's
      // selection or caret statement.
      if (runVisualStatement?.('shortcut')) return;
      // Preserve F5's live Monaco reads and refs fallback exactly. React's
      // selection mirror can lag the editor by one debounce interval.
      const editorHandle = editorRef.current;
      const selectedText = editorHandle
        ? editorHandle.getSelectedText()
        : editorSelectedTextRef.current;
      const selectedTrimmed = selectedText.trim();
      const parsed = getStatementSegments();
      const fullTrimmed = (parsed?.sql ?? queryRef.current).trim();
      if (
        selectedTrimmed &&
        selectedTrimmed !== fullTrimmed &&
        isExecutableStatement(selectedTrimmed)
      ) {
        handleExecuteSelection(selectedText, 'keyboard-shortcut-selection');
        return;
      }
      const cursorStatement = currentCursorStatementRef.current;
      if (
        cursorStatement?.multiStatement &&
        isLikelyCompleteStatement(cursorStatement.text, engineId)
      ) {
        const cursorOffset = editorHandle?.getCursorOffset() ?? null;
        const liveStatement = parsed && cursorOffset !== null
          ? findSegmentAtOffset(parsed.segments, cursorOffset)
          : null;
        const statementText = liveStatement &&
          isLikelyCompleteStatement(liveStatement.text, engineId)
          ? liveStatement.text
          : cursorStatement.text;
        handleExecuteSelection(statementText, 'keyboard-shortcut-cursor');
        return;
      }
      handleExecute('keyboard-shortcut');
    },
    'query.executeCurrentStatement': () => {
      if (runVisualStatement?.('shortcut')) return;
      const editorHandle = editorRef.current;
      const execution = resolveCurrentStatementExecution({
        selectedText: editorHandle
          ? editorHandle.getSelectedText()
          : editorSelectedTextRef.current,
        snapshot: getStatementSegments(),
        cursorOffset: editorHandle?.getCursorOffset() ?? null,
        fallbackCursorStatement: currentCursorStatementRef.current,
        setStatusMessage,
      });
      if (!execution) return true;
      handleExecuteSelection(execution.sql, execution.source);
    },
    'query.executeAll': () => handleExecute('keyboard-shortcut'),
    'query.toggleResults': () => setBottomPaneCollapsed((previous) => !previous),
    'query.save': () => handleSaveFile(),
    'query.saveAs': () => handleSaveFileAs(),
    'query.refreshIntellisense': () => refreshIntelliSenseForActiveQueryTab('shortcut'),
  };

  const activeTabIdRef = useRef(activeTabId);
  activeTabIdRef.current = activeTabId;
  useLayoutEffect(() => registerKeybindingTarget({
    scope: 'query-editor',
    tabId,
    handlers: handlersRef.current,
    getEditor: () => createQueryEditorKeybindingEditor(
      () => editorRef.current,
      () => visibleTabRoot(rootRef.current),
    ),
    getRootElement: () => visibleTabRoot(rootRef.current),
    canExecute: (commandId) => {
      if (activeTabIdRef.current !== tabId) return false;
      if (commandId === 'query.refreshIntellisense') {
        return hasActiveQueryTabRefreshTarget();
      }
      return true;
    },
  }), [editorRef, rootRef, tabId]);

  // Listen for a command-palette-dispatched event to toggle this tab's
  // results pane. The listener is active only while this tab is the
  // active workspace tab, so one window-level event targets exactly one
  // query editor.
  useEffect(() => {
    if (activeTabId !== tabId) return;
    const onToggle = () => {
      setBottomPaneCollapsed((prev) => !prev);
    };
    window.addEventListener('jam:query-editor:toggle-results-pane', onToggle);
    return () => {
      window.removeEventListener('jam:query-editor:toggle-results-pane', onToggle);
    };
  }, [activeTabId, tabId, setBottomPaneCollapsed]);
}
